import { DomesticAccommodationUseType, PropertyCategory } from '~/__generated__/__types__'
import { uniq } from 'underscore'
import { PropertyOrderUseType } from '~/app/components/orders/new/types'
import { ConfigBadgeType, TConfigBadgeType } from '~/api/utils/leisure'

interface IOptionBadgeMeta {
  optionBadges: string[]
  useType: DomesticAccommodationUseType | PropertyOrderUseType
}

/**
 * 통합주문 내 배지 노출 정책
 * https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/90261635
 */
export const badgeHidePolicies = {
  // 장바구니 - 숙박 미노출 배지
  onCartProperty: [
    ConfigBadgeType.MILLION_COUPON,
    ConfigBadgeType.SP_MILLION_COUPON,
    ConfigBadgeType.DAY_USE,
  ],
  // 장바구니 - 객실 미노출 배지
  onCartOption: [
    ConfigBadgeType.MILLION_COUPON,
    ConfigBadgeType.SP_MILLION_COUPON,
    ConfigBadgeType.MILLION_COUPON_MOTEL,
    ConfigBadgeType.CANCEL_PENALTY,
    ConfigBadgeType.Y_FLUX,
    ConfigBadgeType.DAY_USE,
  ],
  // 예약, 예약 완료 - 숙박 단위는 아래의 배지 미노출
  onOrderProperty: [
    ConfigBadgeType.MILLION_COUPON,
    ConfigBadgeType.SP_MILLION_COUPON,
    ConfigBadgeType.DAY_USE,
  ],
  onOrderV2Property: [
    ConfigBadgeType.MILLION_COUPON,
    ConfigBadgeType.SP_MILLION_COUPON,
    ConfigBadgeType.DAY_USE,
    ConfigBadgeType.Y_FLUX,
  ],
  // 대실 상품의 경우 숙박대전 배지 미노출
  onDayUse: [
    ConfigBadgeType.MILLION_COUPON,
    ConfigBadgeType.SP_MILLION_COUPON,
    ConfigBadgeType.DAY_USE,
  ],
}

export const isDayUse = (
  useType: DomesticAccommodationUseType | PropertyOrderUseType,
  category: PropertyCategory
): boolean => {
  return useType === DomesticAccommodationUseType.DayUse && category === 'HOTEL'
}

export const filterBadge = (badges: string[], filterArray: TConfigBadgeType[]): string[] => {
  return badges.filter(badge => !filterArray.find(hide => hide === badge))
}

// 대실이면 숙박대전 뱃지는 미노출 한다.
export const hideMillionCouponByRent = (
  badges: string[],
  useType: DomesticAccommodationUseType | PropertyOrderUseType
) => {
  return useType === DomesticAccommodationUseType.DayUse || useType === 'R'
    ? filterBadge(badges, badgeHidePolicies.onDayUse)
    : badges
}

// 장바구니 - 숙박 단위 배지 노출 정책
export const badgeWithPolicyInCartProperty = (propertyBadges: string[]): string[] => {
  return uniq(filterBadge(propertyBadges, badgeHidePolicies.onCartProperty))
}

// 장바구니 - 객실 단위 배지 노출 정책
export const badgeWithPolicyInCartOption = ({
  optionBadges,
  useType,
  ratePlanBadges,
}: { ratePlanBadges: string[] } & IOptionBadgeMeta): string[] => {
  return uniq(
    hideMillionCouponByRent(
      filterBadge(optionBadges, badgeHidePolicies.onCartOption).concat(ratePlanBadges),
      useType
    )
  )
}

// 예약, 예약 완료 노출 정책
export const badgeWithPolicyInOrder = ({
  propertyBadges,
  optionBadges,
  useType,
  ratePlanBadges,
}: { propertyBadges: string[]; ratePlanBadges: string[] } & IOptionBadgeMeta): string[] => {
  return uniq(
    filterBadge(propertyBadges, badgeHidePolicies.onOrderProperty).concat(
      hideMillionCouponByRent(
        filterBadge(optionBadges, badgeHidePolicies.onOrderProperty).concat(ratePlanBadges),
        useType
      )
    )
  )
}

/**
 * 국내 주문서 V2
 */
export const badgeWithPolicyInOrderV2 = ({
  propertyBadges,
  optionBadges,
  useType,
  ratePlanBadges,
}: { propertyBadges: string[]; ratePlanBadges: string[] } & IOptionBadgeMeta): string[] => {
  return uniq(
    filterBadge(propertyBadges, badgeHidePolicies.onOrderV2Property).concat(
      hideMillionCouponByRent(
        filterBadge(optionBadges, badgeHidePolicies.onOrderProperty).concat(ratePlanBadges),
        useType
      )
    )
  )
}

export const getHasDayUseBadge = (badges: string[] = []) => {
  return badges.includes(ConfigBadgeType.DAY_USE)
}
